/**
 * @export
 * @class Timeline
 */
// @ts-ignore
import * as Odometer from 'odometer';

import { get, getAll } from '../../../helpers/utilities';
import { Swipe } from '../../../modules/swipe';

export class Timeline {
    private isBusy = false;
    private ui: TimelineUI;
    private currentIndex = 0;
    private data: TimelineItem[];


    /**
     * Creates an instance of Timeline.
     *
     * @param {HTMLElement} container The containing element for this block.
     * @memberof Timeline
     */
    constructor(container: HTMLElement) {
        this.data = this.getDataObject() as TimelineItem[];
        this.ui   = this.getUI(container);
        this.init();
    }


    /**
     * Binds the various events for this class and sets up our interactions.
     *
     * @private
     * @memberof Timeline
     */
    private init(): void {

        // On key press
        document.addEventListener('keyup', (event) => {
            switch(event.key) {

                // Down or right arrow key goes forward
                case 'ArrowDown':
                case 'Down':
                case 'ArrowRight':
                case 'Right':
                    this.next();
                    break;

                // Up or left arrow key goes backwards
                case 'ArrowUp':
                case 'Up':
                case 'ArrowLeft':
                case 'Left':
                    this.previous();
                    break;
            }
        });

        // Scroll
        this.ui.container.addEventListener('wheel', (event: WheelEvent) => {
            this.handleScrollEvent((event.deltaY || event.deltaX));
        });

        // On dot click
        this.ui.dots.forEach((dot, index) => {
            dot.addEventListener('click', () => {
                this.goTo(index);
            });
        });

        // On Swipe
        new Swipe({
            onDown:  () => { this.previous() },
            onUp:    () => { this.next() },
            onRight: () => { this.previous() },
            onLeft:  () => { this.next() },
        })

        // Init the odometers
        if(this.ui.odometers) {
            this.ui.odometers.forEach(odometer => {

                new Odometer({
                    el: odometer,
                    format: '',
                    duration: 1000,
                    value: odometer.innerHTML
                });
            });
        }
    }


    /**
     * Moves forward through the timeline.
     *
     * @private
     * @memberof Timeline
     */
    private next(): void {
        if(this.isBusy) return;

        // Get the next item and switch if it exists
        const item = this.data[this.currentIndex + 1];
        if(item) {
            this.isBusy = true;
            this.currentIndex++;
            this.changeTo(item, 'next');
        }
    }


    /**
     * Moves backwards through the timeline.
     *
     * @private
     * @memberof Timeline
     */
    private previous(): void {
        if(this.isBusy) return;

        // Get the next item and switch if it exists
        const item = this.data[this.currentIndex - 1];
        if(item) {
            this.isBusy = true;
            this.currentIndex--;
            this.changeTo(item, 'previous');
        }
    }


    /**
     * Goes to the item with the provided index.
     *
     * @private
     * @param {number} index The index of the item to go to.
     * @memberof Timeline
     */
    private goTo(index: number): void {
        if(this.isBusy || index === this.currentIndex) return;

        // Get the next item and switch if it exists
        const item = this.data[index];
        if(item) {
            this.isBusy = true;
            const currentIndex = this.currentIndex;
            this.currentIndex  = index;
            this.changeTo(item, index > currentIndex ? 'next' : 'previous');
        }
    }


    /**
     * Changes the currently visible item to the provided one.
     *
     * @private
     * @param {TimelineItem} item The item to change to.
     * @param {string} direction The direction the new item is transitioning from.
     * @memberof Timeline
     */
    private changeTo(item: TimelineItem, direction: string): void {
        const yearUpdated  = this.updateYear(item.year);
        const imageUpdated = this.updateImage(item.image, direction);
        const titleUpdated = this.updateTitle(item.title);
        const introUpdated = this.updateIntro(item.description);
        const dotsUpdated  = this.updateDots();

        Promise.all([yearUpdated, imageUpdated, titleUpdated, introUpdated, dotsUpdated])
            .then(() => this.isBusy = false)
            .catch(() => this.isBusy = false);
    }


    /**
     * Transitions between the current image and the new one.
     *
     * @private
     * @param {TimelineItem['image']} image The image object.
     * @param {string} direction Which direction to display the image from
     * @returns {Promise<boolean>}
     * @memberof Timeline
     */
    private updateImage(image: TimelineItem['image'], direction: string): Promise<boolean> {
        return new Promise(resolve => {
            const targetUI = direction === 'next' ? this.ui.backgroud.fromBottom : this.ui.backgroud.fromTop;

            // Allow transitions again
            targetUI.container.classList.remove('u-no-transition');
    
            // Set the src and alt
            targetUI.image.src = image.src;
            targetUI.image.alt = image.alt;
    
            // Display the new image
            targetUI.container.classList.add('c-our-story__image-transition-block--is-visible');
    
            // Once it's finished, change the static image to the new one and reset the size of the targetUI container
            const transitionDuration = parseFloat(getComputedStyle(targetUI.container).transitionDuration) * 1000;
            setTimeout(() => {
    
                // Change the static image
                this.ui.backgroud.static.src = image.src;
                this.ui.backgroud.static.alt = image.alt;
    
                // Reset the targetUI container
                setTimeout(() => {

                    // Reset the height
                    targetUI.container.classList.add('u-no-transition');
                    targetUI.container.classList.remove('c-our-story__image-transition-block--is-visible');

                    // Fin.
                    setTimeout(() => { resolve() }, 200);
                }, 100);
            }, transitionDuration);
        });
    }


    /**
     * Updates the positioning of the dots.
     *
     * @private
     * @returns {Promise<boolean>}
     * @memberof Timeline
     */
    private updateDots(): Promise<boolean> {
        return new Promise(resolve => {
            const targetDotIndex = this.currentIndex;
            
            // Move all the dots along by X amount
            const translateAmount = targetDotIndex * 100;
            this.ui.dots.forEach(dot => {
                const parent = dot.parentNode as HTMLElement;
                if(parent) {
                    parent.style.transform = `translateX(-${translateAmount}%) translateX(10px)`;
                    parent.classList.remove('c-our-story__progress-bar-item--is-active');
                }
            });

            // Set the active class
            const targetParent = this.ui.dots[targetDotIndex].parentNode as HTMLElement;
            if(targetParent) targetParent.classList.add('c-our-story__progress-bar-item--is-active');

            // Once it's finished, resolce
            const transitionDuration = parseFloat(getComputedStyle(targetParent).transitionDuration) * 1000;
            setTimeout(() => { resolve() }, transitionDuration);
        });
    }


    /**
     * Updates the currently displayed year.
     *
     * @private
     * @param {string} year The year to update to.
     * @returns {Promise<boolean>}
     * @memberof Timeline
     */
    private updateYear(year: string): Promise<boolean> {
        return new Promise(resolve => {
            if(this.ui.year) {
                const yearParts = year.split('-');
    
                // Add our remove our class
                yearParts.length === 1 ? this.ui.year.classList.remove('c-our-story__year--has-multiple-parts') : this.ui.year.classList.add('c-our-story__year--has-multiple-parts');
                
                // Update the years
                yearParts.forEach((yearPart, index) => {
                    this.ui.odometers[index].innerHTML = yearPart;
                    resolve();
                });
            }
        });
    }


    /**
     * Display the new title
     *
     * @private
     * @param {string} newTitle The new title to display.
     * @returns {Promise<boolean>}
     * @memberof Timeline
     */
    private updateTitle(newTitle: string): Promise<boolean> {
        return new Promise(resolve => {

            // Hide the title.
            this.ui.title.classList.add('c-our-story__title--is-hidden');

            // Once it's hidden, change the value
            const transitionDuration = parseFloat(getComputedStyle(this.ui.title).transitionDuration) * 1000;
            setTimeout(() => {
                this.ui.title.innerHTML = newTitle;

                // After a short delay, show it again
                setTimeout(() => {
                    this.ui.title.classList.remove('c-our-story__title--is-hidden');
                    resolve();
                }, 100);
            }, transitionDuration);
        });
    }


    /**
     * Displays the new introduction.
     *
     * @private
     * @param {string} newDesc The new introduction to display.
     * @returns {Promise<boolean>}
     * @memberof Timeline
     */
    private updateIntro(newDesc: string): Promise<boolean> {
        return new Promise(resolve => {

            // Hide the description.
            this.ui.description.classList.add('c-our-story__intro--is-hidden');
    
            // Once it's hidden, change the value
            const transitionDuration = parseFloat(getComputedStyle(this.ui.description).transitionDuration) * 1000;
            setTimeout(() => {
                this.ui.description.innerHTML = newDesc;
    
                // After a short delay, show it again
                setTimeout(() => {
                    this.ui.description.classList.remove('c-our-story__intro--is-hidden');
                    resolve();
                }, 20);
            }, transitionDuration);
        });
    }


    /**
     * Checks if we are scrolling forwards or backwards and goes to the next or
     * prev slide respectively
     *
     * @private
     * @param {number} delta The delta of the scroll
     * @memberof Timeline
     */
    private handleScrollEvent(delta: number): void {
        if(delta > 3 ) {
            this.next();
        } else if(delta < -3) {
            this.previous();
        }
    }


    /**
     * Returns the data for the timeline as an object.
     *
     * @private
     * @returns {(TimelineItem[] | null)}
     * @memberof Timeline
     */
    private getDataObject(): TimelineItem[] | null {
        const dataScript = get('#js-our-story__data') as HTMLScriptElement;
        return dataScript ? JSON.parse(dataScript.innerHTML) : null;
    }


    /**
     * Returns the UI elements for this component as an object
     *
     * @private
     * @param {HTMLElement} container The containing element for this block.
     * @returns {TimelineUI}
     * @memberof Timeline
     */
    private getUI(container: HTMLElement): TimelineUI {

        return {
            container,
            year: get('#js-our-story__year') as HTMLElement,
            title: get('#js-our-story__title') as HTMLElement,
            dots: getAll('.js-our-story__date-dot') as HTMLElement[],
            description: get('#js-our-story__desc') as HTMLElement,
            odometers: getAll('.js-our-story__year-part') as HTMLElement[],
            backgroud: {
                static: get('#js-our-story__static-image') as HTMLImageElement,
                fromTop: {
                    container: get('#js-our-story__top-transition') as HTMLElement,
                    image: get('#js-our-story__top-transition-image') as HTMLImageElement
                },
                fromBottom: {
                    container: get('#js-our-story__bottom-transition') as HTMLElement,
                    image: get('#js-our-story__bottom-transition-image') as HTMLImageElement
                }
            }
        }
    }
}
